import httpClient from 'api/https-client';
import { MouseEvent, useCallback } from 'react';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

/**
 * Shortcode an admin pastes into a bonus link's URL field in the admin panel.
 * When an MVP / gold member clicks that link in the rendered manual, we intercept it
 * and mint a fresh CoachesOnline handoff URL — the same MVP token the old custom element produced.
 *
 * Authoring: in the admin editor, add a normal link and set its URL to this shortcode.
 */
export const COACHESONLINE_MVP_SHORTCODE = '[coachesonline-mvp]';

// Match the core token regardless of how the editor stores/encodes the URL, e.g.
// `[coachesonline-mvp]`, `coachesonline-mvp`, `https://coachesonline-mvp`, `%5Bcoachesonline-mvp%5D`.
const SHORTCODE_TOKEN = 'coachesonline-mvp';

const hrefHasShortcode = (anchor: HTMLAnchorElement): boolean => {
  const raw = (anchor.getAttribute('href') || '').toLowerCase();
  const resolved = (anchor.href || '').toLowerCase();

  return raw.includes(SHORTCODE_TOKEN) || resolved.includes(SHORTCODE_TOKEN);
};

export const useCoachesOnlineHandoff = () => {
  const openHandoff = useCallback(async () => {
    try {
      // Mint a FRESH, short-lived (10 min) handoff URL on every click — never cache it.
      // The endpoint is gold/MVP-gated server-side (403 for everyone else → this no-ops).
      const { status, data } = await httpClient.get<{ url: string }>('/coachesonline/handoff-url');

      if (status >= 200 && status < 300 && data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      // Graceful degrade — surface nothing scary to the member; logged for debugging.
      console.error('Failed to build CoachesOnline handoff URL', error);
    }
  }, []);

  /**
   * Delegated click handler — attach to the element that renders manual content.
   * Intercepts clicks on any link whose URL contains the CoachesOnline shortcode and
   * swaps the dead shortcode link for a freshly minted, authenticated handoff.
   */
  const handleManualClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a') as HTMLAnchorElement | null;

      if (!anchor || !hrefHasShortcode(anchor)) {
        return;
      }

      event.preventDefault();
      trackAmplitudeEvent('CoachesOnline Claim Pressed');
      void openHandoff();
    },
    [openHandoff],
  );

  return { handleManualClick };
};
